import React from "react";
import {
  Box,
  Stack,
  Button,
  Typography,
  Container,
  Grid,
  Card,
  CardContent,
  CardActions,
  Paper,
  Divider,
} from "@mui/material";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";

import WaterlooLogo from "../../util/WaterlooLogo";

const quick_links = [
  {
    name: "Virtual Toolbox",
    image: "/images/resource-logos/toolbox.png",
    image_alt: "UWaterloo Portal Logo",
    link: "https://linktr.ee/engtoolbox",
    blurb: "Your go-to hub for all Engineering O-Week resources",
  },
  {
    name: "Portal",
    image: "/images/resource-logos/portal.png",
    image_alt: "UWaterloo Portal Logo",
    link: "https://portal.uwaterloo.ca/",
    blurb:
      "University of Waterloo Portal is an excellent resource for students to access their schedules, WatCard info, campus maps, and wellness resources!",
  },
  {
    name: "Learn",
    image: "/images/resource-logos/learn.png",
    image_alt: "UWaterloo Learn Logo",
    link: "https://learn.uwaterloo.ca/",
    blurb:
      "Learn is the University of Waterloo's online learning management system. This is where all of your course resources will be such as lecture slides, assignments, digital submissions, and grades.",
  },
  {
    name: "Quest",
    image: "/images/resource-logos/quest.png",
    image_alt: "UWaterloo Quest Logo",
    link: "https://uwaterloo.ca/quest/",
    blurb:
      "Quest is the University of Waterloo's student information system. This is where you can view your course selection, final term grades, financial information, and other admin functions.",
  },
  {
    name: "Odyssey",
    icon: <WaterlooLogo style={{ maxHeight: "40px" }} />,
    link: "https://odyssey.uwaterloo.ca/",
    blurb:
      "Odyssey is the University of Waterloo's exam management system. Information for your midterms and finals will be available on Odyssey, such as: Time, location, assigned seat, and permitted materials.",
  },
  {
    name: "Waterloo Works",
    image: "/images/resource-logos/waterloo-works.png",
    image_alt: "UW Waterloo Works Logo",
    link: "https://waterlooworks.uwaterloo.ca/home.htm",
    blurb:
      "Waterloo Works is the University of Waterloo's co-op job board. This is where you will apply to co-op jobs and manage your job applications.",
  },
  {
    name: "Engineering Course Calendar",
    icon: <WaterlooLogo style={{ maxHeight: "40px" }} />,
    link: "https://ugradcalendar.uwaterloo.ca/group/uWaterloo-Faculty-of-Engineering",
    blurb:
      "The Engineering Course Calendar is where you can find information on all of the courses offered by the Faculty of Engineering as well as your department's course requirements to graduate.",
  },
  {
    name: "Campus Wellness",
    icon: <WaterlooLogo style={{ maxHeight: "40px" }} />,
    link: "https://uwaterloo.ca/campus-wellness/",
    blurb:
      "Campus Wellness is the University of Waterloo's health and wellness resource. This is where you can find information on mental health, physical health, and other wellness aspects.",
  },
  {
    name: "WUSA Homepage",
    image: "/images/resource-logos/wusa.png",
    image_alt: "WUSA Logo",
    link: "https://wusa.ca/",
    blurb:
      "The Waterloo Undergraduate Student Association (WUSA) is the official student union for undergraduate students at the University of Waterloo. This is where you can find information on student clubs, events, and other WUSA-provided resources.",
  },
  {
    name: "EngSoc Homepage",
    image: "/images/resource-logos/engsoc.png",
    image_alt: "EngSoc Logo",
    link: "https://www.engsoc.uwaterloo.ca/",
    blurb:
      "The Engineering Society (EngSoc) is the student society for engineering students at the University of Waterloo. This is where you can find information on engineering student clubs, events, and other EngSoc-provided resources.",
  },
];

const Hero = () => (
  <Paper
    elevation={0}
    sx={{
      bgcolor: "#5D0096",
      color: "white",
      borderRadius: 0,
      py: 6,
      mb: 4,
    }}
  >
    <Container maxWidth="lg">
      <Stack spacing={2.5} alignItems="center" textAlign="center">
        <Typography
          variant="h2"
          fontWeight="bold"
          sx={{ fontSize: { xs: "2.2rem", md: "3rem" } }}
        >
          Quick Links
        </Typography>
        <Typography
          variant="h6"
          sx={{ maxWidth: 900, lineHeight: 1.8, opacity: 0.95 }}
        >
          As you get acquainted with campus and its vast resources, here are
          some of the most important resources that you have at your disposal!
        </Typography>
      </Stack>
    </Container>
  </Paper>
);

const QuickLinkCard = ({ item }) => {
  const open = () => window.open(item.link, "_blank", "noopener,noreferrer");

  return (
    <Card
      elevation={3}
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: 3,
        overflow: "hidden",
        transition: "transform 0.2s ease, box-shadow 0.2s ease",
        "&:hover": { transform: "translateY(-4px)", boxShadow: 8 },
      }}
    >
      <Box
        sx={{
          bgcolor: "engSecondary.light",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: 140,
          p: 2,
        }}
      >
        {item.icon ? (
          <Box sx={{ "& svg": { height: 56, width: "auto" } }}>{item.icon}</Box>
        ) : (
          <img
            src={item.image}
            alt={item.image_alt}
            style={{
              maxHeight: "100%",
              maxWidth: "100%",
              objectFit: "contain",
            }}
          />
        )}
      </Box>

      <CardContent sx={{ flexGrow: 1 }}>
        <Stack spacing={1.5}>
          <Typography variant="h6" fontWeight={700}>
            {item.name}
          </Typography>
          <Divider />
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ lineHeight: 1.9 }}
          >
            {item.blurb}
          </Typography>
        </Stack>
      </CardContent>

      <CardActions sx={{ p: 2, pt: 0 }}>
        <Button
          onClick={open}
          variant="contained"
          color="engPrimary"
          endIcon={<ArrowOutwardIcon />}
          fullWidth
          sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
        >
          Open {item.name}
        </Button>
      </CardActions>
    </Card>
  );
};

const QuickLinks = () => {
  return (
    <Box>
      <Hero />

      <Container maxWidth="lg" sx={{ pb: 6 }}>
        <Grid container spacing={3}>
          {quick_links.map((item) => (
            <Grid key={item.name} item xs={12} sm={6} md={4}>
              <QuickLinkCard item={item} />
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default QuickLinks;
